/**
 * Per-CR drill-down popups, shared by all three Log Review screens:
 * session details, servers/nodes, attachments and the justification →
 * review → approval audit history.
 */

import { LOG_REVIEW_URL } from "../constants/index";
import type {
  LogReviewJustificationHistory,
  LogReviewServerNode,
  LogReviewSessionDetail,
} from "../app/types/logReview";
import { logReviewGet } from "./logReviewClient";

// ─── Reason / session-details popup ──────────────────────────────────────

interface RawReasonDetail {
  hostName: string;
  userName: string;
  targetUser: string;
  reason: string;
  complianceType: string;
  actualWindowStart: string;
  actualWindowEnd: string;
  plannedWindowStart: string;
  plannedWindowEnd: string;
}

export const fetchReasonDetails = async (
  changeRequestId: string,
  appCode: string,
  signal?: AbortSignal,
): Promise<LogReviewSessionDetail[]> => {
  const raw = await logReviewGet<RawReasonDetail[]>(
    `${LOG_REVIEW_URL.SIRAR}/getReasonDetailsPopUp/${encodeURIComponent(changeRequestId)}/${encodeURIComponent(appCode)}`,
    signal,
  );
  return (raw ?? []).map((r) => ({
    lanUserId: r.userName,
    serverNode: r.hostName,
    targetAccount: r.targetUser,
    plannedStart: r.plannedWindowStart ?? "",
    plannedEnd: r.plannedWindowEnd ?? "",
    actualStart: r.actualWindowStart ?? "",
    actualEnd: r.actualWindowEnd ?? "",
    status: r.complianceType,
  }));
};

// ─── Servers / nodes popup ───────────────────────────────────────────────

interface RawServerNode {
  host: string;
  team: string;
}

export const fetchServerNodes = async (
  id: string,
  signal?: AbortSignal,
): Promise<LogReviewServerNode[]> => {
  const raw = await logReviewGet<RawServerNode[]>(
    `${LOG_REVIEW_URL.SIRAR}/getNonCompliantServerNodesPopUp/${encodeURIComponent(id)}`,
    signal,
  );
  return (raw ?? []).map((r) => ({ team: r.team, serverCis: r.host }));
};

// ─── Attachments popup ───────────────────────────────────────────────────

/** A supporting document returned by `getFilesByRequestId` (base64 payload). */
export interface LogReviewAttachment {
  requestId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  /** Base64-encoded file contents (no data-URI prefix). */
  fileData: string;
}

/**
 * Attachments for a CR. Shared by all three Log Review dashboards. The path
 * keys off the finding `id` and the `requestId` (e.g. `SCR260605-0001`).
 */
export const fetchAttachments = async (
  id: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<LogReviewAttachment[]> => {
  const raw = await logReviewGet<LogReviewAttachment[]>(
    `${LOG_REVIEW_URL.SIRAR}/getFilesByRequestId/${encodeURIComponent(id)}/${encodeURIComponent(requestId)}`,
    signal,
  );
  return raw ?? [];
};

// ─── Info / justification-history popup ──────────────────────────────────

interface RawInfoDetail {
  requestedBy: string | null;
  userComment: string | null;
  requestTime: string | null;
  teamLeadReviewStatus: string | null;
  teamLeadComment: string | null;
  teamLeadReviewTime: string | null;
  acknowledgedBy: string | null;
  divisionHeadReviewStatus: string | null;
  divisionHeadComment: string | null;
  divisionHeadReviewTime: string | null;
  reviewedBy: string | null;
  attachments: unknown[] | null;
}

export const fetchInfoDetails = async (
  id: string,
  signal?: AbortSignal,
): Promise<LogReviewJustificationHistory[]> => {
  const raw = await logReviewGet<RawInfoDetail[]>(
    `${LOG_REVIEW_URL.SIRAR}/getInfoPopUp/${encodeURIComponent(id)}`,
    signal,
  );
  return (raw ?? []).map((r) => ({
    justification: r.userComment ?? "—",
    justifiedBy: r.requestedBy ?? "—",
    justifiedOn: r.requestTime ?? "—",
    attachments: (r.attachments ?? []).length ? `${(r.attachments ?? []).length}` : "—",
    reviewerComment: r.teamLeadComment ?? "—",
    reviewerCommentedBy: r.acknowledgedBy ?? "—",
    reviewerCommentedOn: r.teamLeadReviewTime ?? "—",
    approverComment: r.divisionHeadComment ?? "—",
    approverCommentedBy: r.reviewedBy ?? "—",
    approverCommentedOn: r.divisionHeadReviewTime ?? "—",
    // Fall back to the team-lead stage (then a dash) when the division head
    // hasn't reviewed yet — avoids a null status in the audit row.
    status: r.divisionHeadReviewStatus ?? r.teamLeadReviewStatus ?? "—",
  }));
};
